import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Bug,
  Calendar,
  CheckCircle2,
  HelpCircle,
  Lightbulb,
  Loader2,
  Mail,
  RotateCcw,
  Save,
  Send,
  type LucideIcon,
} from 'lucide-react'
import { PlatformPage } from './PlatformPage'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import { describeStatus } from '@/features/contactSales/status'
import { orderedPayload } from '@/features/contactSales/payloadOrder'
import { StatusTimeline } from '@/features/contactSales/StatusTimeline'
import {
  useContactMessage,
  useReplyContactMessage,
  useUpdateContactMessage,
} from '@/features/platformAdmin/queries'
import type {
  ContactMessageCategory,
  ContactMessageReply,
  ContactMessageStatus,
} from '@/features/platformAdmin/types'

const CATEGORY_META: Record<ContactMessageCategory, { label: string; icon: LucideIcon }> = {
  sales:   { label: 'Talk to Sales',     icon: Mail },
  bug:     { label: 'Report an Issue',   icon: Bug },
  feature: { label: 'Request a Feature', icon: Lightbulb },
  support: { label: 'Get Help',          icon: HelpCircle },
}

const STATUS_LABEL: Record<ContactMessageStatus, string> = {
  requested: 'Requested',
  viewed: 'Viewed',
  in_progress: 'In progress',
  planned: 'Planned',
  already_available: 'Already in app',
  shipped: 'Shipped',
  wont_do: "Won't do",
  closed: 'Closed',
}

const STATUS_BY_CATEGORY: Record<ContactMessageCategory, ContactMessageStatus[]> = {
  sales:   ['requested', 'viewed', 'in_progress', 'planned', 'shipped', 'wont_do', 'closed'],
  bug:     ['requested', 'viewed', 'in_progress', 'planned', 'already_available', 'shipped', 'wont_do', 'closed'],
  feature: ['requested', 'viewed', 'in_progress', 'planned', 'already_available', 'shipped', 'wont_do', 'closed'],
  support: ['requested', 'viewed', 'in_progress', 'planned', 'shipped', 'wont_do', 'closed'],
}

const fmtDateTime = (v: string | null) => (v ? formatDateTime(v) : '-')

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function ContactMessageDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canManage = hasPermission('admin.contact_messages.manage')

  const detailQ = useContactMessage(Number.isFinite(id) ? id : null)
  const updateM = useUpdateContactMessage()
  const replyM = useReplyContactMessage()

  const message = detailQ.data?.message ?? null
  const replies = detailQ.data?.replies ?? []
  const scrollRef = useRef<HTMLDivElement>(null)

  const [draftStatus, setDraftStatus] = useState<ContactMessageStatus | null>(null)
  const [draftNotes, setDraftNotes] = useState('')
  const [draftDecisionNote, setDraftDecisionNote] = useState('')
  const [draftTargetDate, setDraftTargetDate] = useState('')
  const [reply, setReply] = useState('')

  // Sync local drafts when the row first loads or after a save reloads it.
  useEffect(() => {
    if (!message) return
    setDraftStatus(message.status)
    setDraftNotes(message.notes ?? '')
    setDraftDecisionNote(message.decision_note ?? '')
    setDraftTargetDate(message.target_release_date ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.id])

  // Auto-scroll to the latest reply on first load and on every new bubble.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [replies.length])

  const onSave = async () => {
    if (!message || !canManage) return
    const changes: {
      status?: ContactMessageStatus
      notes?: string | null
      decisionNote?: string | null
      targetReleaseDate?: string | null
    } = {}
    if (draftStatus && draftStatus !== message.status) changes.status = draftStatus
    if ((draftNotes || '') !== (message.notes || '')) changes.notes = draftNotes
    if ((draftDecisionNote || '') !== (message.decision_note || '')) {
      changes.decisionNote = draftDecisionNote
    }
    if ((draftTargetDate || '') !== (message.target_release_date || '')) {
      changes.targetReleaseDate = draftTargetDate || null
    }
    if (!Object.keys(changes).length) {
      toast({ title: 'Nothing to save.' })
      return
    }
    try {
      await updateM.mutateAsync({ id: message.id, body: changes })
      toast({ title: 'Saved.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Save failed', description: errMsg(err) })
    }
  }

  const onSendReply = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!message || !reply.trim()) return
    const text = reply.trim()
    setReply('')
    try {
      await replyM.mutateAsync({ id: message.id, body: text })
    } catch (err) {
      setReply(text)
      toast({ variant: 'destructive', title: 'Reply failed', description: errMsg(err) })
    }
  }

  const onToggleClosed = async () => {
    if (!message || !canManage) return
    try {
      await updateM.mutateAsync({
        id: message.id,
        body: { status: message.status === 'closed' ? 'in_progress' : 'closed' },
      })
      toast({ title: message.status === 'closed' ? 'Re-opened.' : 'Closed.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not update status', description: errMsg(err) })
    }
  }

  const headerDesc = message ? describeStatus(message.status, message.category) : null
  const HeaderIcon = headerDesc?.icon

  return (
    <PlatformPage
      title="Contact message"
      description={message ? CATEGORY_META[message.category].label : undefined}
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/platform-admin/contact-messages')}>
            <ArrowLeft className="size-4" />
            Back to inbox
          </Button>
          {canManage && message ? (
            message.status === 'closed' ? (
              <Button
                variant="secondary"
                onClick={onToggleClosed}
                disabled={updateM.isPending}
              >
                {updateM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RotateCcw className="size-4" />
                )}
                Re-open
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={onToggleClosed}
                disabled={updateM.isPending}
              >
                {updateM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="size-4" />
                )}
                Close query
              </Button>
            )
          ) : null}
        </>
      }
    >
      {detailQ.isLoading ? (
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : detailQ.isError || !message ? (
        <Alert variant="destructive">
          <AlertDescription>Could not load this message.</AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            {/* Header card */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <CategoryIcon category={message.category} />
                    {message.subject || 'Untitled message'}
                  </CardTitle>
                  {headerDesc && HeaderIcon ? (
                    <Badge variant={headerDesc.badgeVariant} className="gap-1">
                      <HeaderIcon className="size-3" />
                      {STATUS_LABEL[message.status]}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  Submitted {fmtDateTime(message.created_at)} by {message.email}
                  {message.tenant_name ? ` · Tenant: ${message.tenant_name}` : ''}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const ordered = orderedPayload(message.payload, message.category)
                  if (ordered.length === 0) return null
                  return (
                    <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-xs sm:grid-cols-2">
                      {ordered.map(([k, v]) => (
                        <div key={k}>
                          <span className="font-medium uppercase tracking-wide text-muted-foreground">
                            {prettyKey(k)}:&nbsp;
                          </span>
                          <span className="break-words">{v}</span>
                        </div>
                      ))}
                    </div>
                  )
                })()}
                <p className="whitespace-pre-wrap text-sm">{message.description}</p>
              </CardContent>
            </Card>

            {/* Conversation */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Conversation</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  ref={scrollRef}
                  className="max-h-[420px] space-y-3 overflow-y-auto pr-1"
                >
                  {replies.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No replies yet. Send the first message below - it will be visible to
                      the customer in their Contact us → Your queries view.
                    </p>
                  ) : (
                    replies.map((r) => <ReplyBubble key={r.id} reply={r} />)
                  )}
                </div>
              </CardContent>
            </Card>

            {canManage ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Reply to customer</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={onSendReply} className="space-y-3">
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.currentTarget.value)}
                      placeholder="Reply to the customer (visible in their Contact us → Your queries view)…"
                      className="min-h-[120px]"
                    />
                    <div className="flex justify-end">
                      <Button type="submit" disabled={replyM.isPending || !reply.trim()}>
                        {replyM.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Send className="size-4" />
                        )}
                        Send reply
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : null}
          </div>

          {/* Right sidebar - status timeline + admin controls */}
          <aside className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <StatusTimeline message={message} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {canManage ? 'Manage' : 'Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Status
                  </span>
                  {canManage ? (
                    <Select
                      value={draftStatus ?? message.status}
                      onValueChange={(v) => setDraftStatus(v as ContactMessageStatus)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_BY_CATEGORY[message.category].map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={headerDesc?.badgeVariant ?? 'outline'}>
                      {STATUS_LABEL[message.status]}
                    </Badge>
                  )}
                </div>

                {/* Target release date is only meaningful for bug fixes and
                    feature requests - work that ships in a Bokapsys release.
                    Sales inquiries and support questions don't have a
                    "release", so the field would just be confusing. */}
                {message.category === 'bug' || message.category === 'feature' ? (
                  <div className="space-y-1">
                    <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <Calendar className="size-3" />
                      Target release date
                    </span>
                    {canManage ? (
                      <Input
                        type="date"
                        value={draftTargetDate}
                        onChange={(e) => setDraftTargetDate(e.currentTarget.value)}
                      />
                    ) : (
                      <p className="text-sm">{message.target_release_date || '-'}</p>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Visible to the customer when status is Planned.
                    </p>
                  </div>
                ) : null}

                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Customer-visible note
                  </span>
                  {canManage ? (
                    <Textarea
                      value={draftDecisionNote}
                      onChange={(e) => setDraftDecisionNote(e.currentTarget.value)}
                      placeholder="e.g. Already supported on the Reconciliation page; ships in v3.2…"
                      className="min-h-[70px]"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
                      {message.decision_note || '-'}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Internal notes{' '}
                    <span className="font-normal lowercase text-muted-foreground/80">
                      (staff only)
                    </span>
                  </span>
                  {canManage ? (
                    <Textarea
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.currentTarget.value)}
                      placeholder="Triage notes, links to tickets, who responded…"
                      className="min-h-[80px]"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
                      {message.notes || '-'}
                    </p>
                  )}
                </div>

                {canManage ? (
                  <Button onClick={onSave} disabled={updateM.isPending} className="w-full">
                    {updateM.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    Save changes
                  </Button>
                ) : null}

                <div className="grid grid-cols-1 gap-2 border-t pt-3 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium uppercase tracking-wide">Viewed</span>
                    <p>{fmtDateTime(message.viewed_at)}</p>
                  </div>
                  <div>
                    <span className="font-medium uppercase tracking-wide">Last action</span>
                    <p>
                      {fmtDateTime(message.handled_at)}
                      {message.handled_by_email ? ` by ${message.handled_by_email}` : ''}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      )}

    </PlatformPage>
  )
}

function CategoryIcon({ category }: { category: ContactMessageCategory }) {
  const Icon = CATEGORY_META[category].icon
  return <Icon className="size-5 text-muted-foreground" />
}

function ReplyBubble({ reply }: { reply: ContactMessageReply }) {
  const isStaff = reply.author_type === 'staff'
  // Staff perspective: own replies on the right, customer replies on the
  // left. Meta line sits UNDER the bubble, edge-aligned to its side.
  return (
    <div className={`flex flex-col gap-1 ${isStaff ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm text-foreground ${
          isStaff ? 'border-primary/20 bg-primary/10' : 'border-border bg-muted'
        }`}
      >
        <p className="whitespace-pre-wrap">{reply.body}</p>
      </div>
      <div className="flex items-center gap-2 px-1 text-[11px]">
        <span className={`font-medium ${isStaff ? 'text-primary' : 'text-foreground'}`}>
          {isStaff ? 'Bokapsys' : reply.author_name || reply.author_email || 'Customer'}
        </span>
        <span className="text-muted-foreground">{fmtDateTime(reply.created_at)}</span>
      </div>
    </div>
  )
}

function prettyKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}
